import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollText, ShieldAlert } from 'lucide-react';

const ACTION_LABELS = {
  registro: { label: 'Registro de cuenta', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  inicio_sesion: { label: 'Inicio de sesión', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
  crear_tanda: { label: 'Creación de tanda', cls: 'bg-green-100 text-green-700 border-green-200' },
  editar_tanda: { label: 'Edición de tanda', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  eliminar_tanda: { label: 'Eliminación de tanda', cls: 'bg-red-100 text-red-700 border-red-200' },
  solicitud_union: { label: 'Solicitud de unión', cls: 'bg-violet-100 text-violet-700 border-violet-200' },
  aprobar_miembro: { label: 'Aprobación de miembro', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  rechazar_miembro: { label: 'Rechazo de miembro', cls: 'bg-rose-100 text-rose-700 border-rose-200' },
  subir_comprobante: { label: 'Subida de comprobante', cls: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
  validar_pago: { label: 'Validación de pago', cls: 'bg-green-100 text-green-700 border-green-200' },
  rechazar_pago: { label: 'Rechazo de pago', cls: 'bg-red-100 text-red-700 border-red-200' },
  notificar_turno: { label: 'Notificación de turno', cls: 'bg-teal-100 text-teal-700 border-teal-200' },
  recordatorio_pago: { label: 'Recordatorio de pago', cls: 'bg-orange-100 text-orange-700 border-orange-200' },
  actualizar_perfil: { label: 'Actualización de perfil', cls: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
};

export default function AdminAudit() {
  const { api, user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [actionFilter, setActionFilter] = useState('todas');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role === 'admin') fetchLogs();
  }, [user, actionFilter]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = actionFilter !== 'todas' ? `?action=${actionFilter}` : '';
      const { data } = await api.get(`/admin/audit${params}`);
      setLogs(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (user && user.role !== 'admin') {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center space-y-4" data-testid="audit-access-denied">
        <ShieldAlert className="w-12 h-12 text-red-500" />
        <p className="text-lg font-medium">Esta sección es solo para administradores.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="audit-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
            <ScrollText className="text-primary" /> Auditoría
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Registro de todas las acciones de usuaries en la plataforma.</p>
        </div>
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger data-testid="audit-action-filter" className="w-64"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todas">Todas las acciones</SelectItem>
            {Object.entries(ACTION_LABELS).map(([key, v]) => (
              <SelectItem key={key} value={key}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Historial</CardTitle>
          <CardDescription>{logs.length} registro{logs.length !== 1 ? 's' : ''} — los más recientes primero</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center py-10 animate-pulse text-muted-foreground">Cargando registros...</p>
          ) : logs.length === 0 ? (
            <p className="text-center py-10 text-muted-foreground" data-testid="audit-empty">No hay registros de auditoría aún.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="audit-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Usuarie</TableHead>
                    <TableHead>Acción</TableHead>
                    <TableHead>Detalles</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map(log => {
                    const meta = ACTION_LABELS[log.action] || { label: log.action, cls: '' };
                    return (
                      <TableRow key={log._id} data-testid={`audit-row-${log._id}`}>
                        <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                          {new Date(log.timestamp).toLocaleString('es-MX')}
                        </TableCell>
                        <TableCell>
                          <p className="font-medium text-sm">{log.user_name || '—'}</p>
                          <p className="text-xs text-muted-foreground">{log.user_email}</p>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={meta.cls}>{meta.label}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground max-w-xs">{log.details || '—'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
